import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import * as Icons from 'lucide-react';
import { CheckCircle2, Clock, Lock, Infinity as InfIcon, TrendingUp } from 'lucide-react';
import { usePlatformSettings } from '@/lib/platformSettings';

/**
 * WaysToEarnPoints — the transparency section for the /dashboard/invite page.
 * Consumes the /api/points/my-progress response.
 */

const CAT_META = {
  onboarding: { title: 'Onboarding rewards', subtitle: 'Complete these one-time actions to earn your first points', badge: 'One-time' },
  recurring: { title: 'Recurring rewards', subtitle: 'Keep earning every month by staying active (capped monthly)', badge: 'Recurring' },
  referral: { title: 'Referral rewards', subtitle: 'Auto-credited when someone signs up with your code and reaches each stage — UNCAPPED', badge: 'Uncapped' },
  cashback: { title: 'Purchase cashback', subtitle: 'Every purchase pays you back in points — UNCAPPED', badge: 'Uncapped' },
  community: { title: 'Community (coming soon)', subtitle: 'Forums, Kaizen contests, expert ranks — more ways to earn coming up', badge: 'Coming soon' },
};

const STATUS_META = {
  completed: { icon: CheckCircle2, color: 'text-[hsl(var(--verified))]', label: 'Earned' },
  pending: { icon: Clock, color: 'text-yellow-600 dark:text-yellow-500', label: 'Pending' },
  active: { icon: TrendingUp, color: 'text-primary', label: 'Active' },
  capped: { icon: CheckCircle2, color: 'text-[hsl(var(--verified))]', label: 'Cap reached' },
  locked: { icon: Lock, color: 'text-muted-foreground', label: 'Coming soon' },
};

const RuleRow = ({ rule }) => {
  const Icon = Icons[rule.icon] || Icons.Circle;
  const st = STATUS_META[rule.status] || STATUS_META.pending;
  const StatusIcon = st.icon;
  const displayPoints = rule.is_percentage ? `${rule.percentage}%` : `+${rule.points} pts`;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${rule.status === 'completed' || rule.status === 'capped' ? 'bg-[hsl(var(--verified))]/5 border-[hsl(var(--verified))]/30' : rule.status === 'locked' ? 'bg-muted/40 opacity-70' : 'hover:bg-secondary'}`}
      data-testid={`earn-rule-${rule.id}`}
    >
      <div className={`shrink-0 h-9 w-9 rounded-md flex items-center justify-center ${rule.status === 'locked' ? 'bg-muted' : 'bg-primary/10'}`}>
        <Icon className={`h-4.5 w-4.5 ${rule.status === 'locked' ? 'text-muted-foreground' : 'text-primary'}`} />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-1.5 flex-wrap'>
          <span className='font-medium text-sm'>{rule.label}</span>
          {rule.coming_soon && <Badge variant='outline' className='text-[9px] uppercase h-4'>Coming soon</Badge>}
          {rule.auto_credited && <Badge variant='outline' className='text-[9px] uppercase h-4 border-primary/40 text-primary'>Auto</Badge>}
        </div>
        <div className='text-xs text-muted-foreground mt-0.5'>{rule.description}</div>
        {rule.progress && rule.monthly_cap > 0 && (
          <div className='mt-2 flex items-center gap-2'>
            <Progress value={(rule.progress.earned / rule.progress.cap) * 100} className='h-1.5 flex-1' />
            <span className='text-[11px] text-muted-foreground tabular-nums'>{rule.progress.earned}/{rule.progress.cap} pts this month</span>
          </div>
        )}
      </div>
      <div className='text-right shrink-0'>
        <div className={`text-sm font-semibold ${rule.status === 'locked' ? 'text-muted-foreground' : 'text-primary'}`}>{displayPoints}</div>
        <div className={`text-[10px] uppercase flex items-center gap-1 justify-end ${st.color}`}>
          <StatusIcon className='h-3 w-3' /> {st.label}
        </div>
      </div>
    </div>
  );
};

export const WaysToEarnPoints = ({ progress }) => {
  const { feature_toggles } = usePlatformSettings();
  const showUpcoming = !!feature_toggles?.show_upcoming_earn_categories;
  if (!progress) return null;
  const { grouped, global_monthly_cap, global_monthly_earned, points_per_inr } = progress;
  const capPct = global_monthly_cap > 0 ? Math.min(100, Math.round((global_monthly_earned / global_monthly_cap) * 100)) : 0;
  const capValueInr = global_monthly_cap > 0 ? Math.round(global_monthly_cap / points_per_inr) : 0;
  const capEarnedInr = Math.round(global_monthly_earned / points_per_inr);

  // 'community' includes forum-question, Kaizen and other roadmap items. We
  // keep them out of the customer-facing list until launch (admin can
  // enable via Admin → Settings → feature_toggles).
  const order = showUpcoming
    ? ['onboarding', 'recurring', 'referral', 'cashback', 'community']
    : ['onboarding', 'recurring', 'referral', 'cashback'];

  return (
    <div className='space-y-4' data-testid='ways-to-earn-section'>
      <div>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <TrendingUp className='h-5 w-5 text-primary' /> Ways to earn points
        </h2>
        <p className='text-sm text-muted-foreground'>Full transparency on every way you can earn points on IndusFact.</p>
      </div>

      {/* Global monthly cap progress */}
      {global_monthly_cap > 0 && (
        <Card className='p-4' data-testid='global-cap-progress'>
          <div className='flex items-start justify-between gap-3 flex-wrap mb-2'>
            <div>
              <div className='text-sm font-semibold'>This month’s earning cap</div>
              <div className='text-xs text-muted-foreground'>Applies to platform activities. Referrals & cashback are UNCAPPED <InfIcon className='h-3 w-3 inline text-primary' />.</div>
            </div>
            <div className='text-right'>
              <div className='text-sm font-semibold tabular-nums'>{global_monthly_earned} / {global_monthly_cap} <span className='text-xs text-muted-foreground'>pts</span></div>
              <div className='text-[11px] text-muted-foreground'>≈ ₹{capEarnedInr} of ₹{capValueInr} this month</div>
            </div>
          </div>
          <Progress value={capPct} className='h-2' />
          {capPct >= 100 && (
            <div className='mt-2 text-xs text-yellow-700 dark:text-yellow-500'>
              You’ve hit your monthly cap. Activities still track for badges & streaks — points resume next month.
            </div>
          )}
        </Card>
      )}

      {/* Grouped by category */}
      {order.map((cat) => {
        const rules = grouped[cat];
        if (!rules || rules.length === 0) return null;
        const meta = CAT_META[cat] || { title: cat, subtitle: '', badge: '' };
        return (
          <Card key={cat} className='p-4' data-testid={`earn-category-${cat}`}>
            <div className='flex items-start justify-between gap-2 flex-wrap mb-3'>
              <div>
                <div className='font-semibold text-sm sm:text-base'>{meta.title}</div>
                <div className='text-xs text-muted-foreground'>{meta.subtitle}</div>
              </div>
              {meta.badge && (
                <Badge variant='outline' className={`text-[10px] uppercase ${meta.badge === 'Uncapped' ? 'border-[hsl(var(--verified))]/40 text-[hsl(var(--verified))]' : meta.badge === 'Coming soon' ? 'border-muted-foreground/30 text-muted-foreground' : 'border-primary/40 text-primary'}`}>
                  {meta.badge}
                </Badge>
              )}
            </div>
            <div className='grid gap-2 md:grid-cols-2'>
              {rules.map((r) => <RuleRow key={r.id} rule={r} />)}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default WaysToEarnPoints;
